import { useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Download, Loader2, Paperclip, Reply, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { usePermission } from "@/hooks/usePermission";
import { useApiSWR } from "@/hooks/useApiSWR";
import { useProjectMembers } from "@/hooks/useProjectMembers";
import { getInitials, profileColorClass } from "./types";
import { CommentComposer } from "./CommentComposer";
import {
	MarkdownPreview,
	MENTION_HREF,
} from "@/components/markdown-editor/MarkdownPreview";
import { type ProjectMember } from "@/services/project-member.service";
import {
	type TaskComment,
	listTaskComments,
	createTaskComment,
	deleteTaskComment,
} from "@/services/task-comment.service";
import {
	type CommentAttachment,
	uploadCommentAttachment,
	deleteCommentAttachment,
} from "@/services/comment-attachment.service";

function formatBytes(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

async function uploadFiles(
	projectId: string,
	taskId: string,
	commentId: string,
	files: File[],
): Promise<CommentAttachment[]> {
	const uploaded: CommentAttachment[] = [];
	for (const file of files) {
		try {
			uploaded.push(
				await uploadCommentAttachment(
					projectId,
					taskId,
					commentId,
					file,
				),
			);
		} catch (e) {
			toast.error(`Failed to attach ${file.name}`, {
				description: (e as Error).message,
			});
		}
	}
	return uploaded;
}

interface Props {
	projectId: string;
	taskId: string;
}

interface Thread {
	root: TaskComment;
	replies: TaskComment[];
}

function timeAgo(iso: string): string {
	try {
		return formatDistanceToNow(new Date(iso), { addSuffix: true });
	} catch {
		return iso;
	}
}

function buildThreads(comments: TaskComment[]): Thread[] {
	const roots = comments.filter((c) => !c.parent_comment_id);
	const repliesByParent = new Map<string, TaskComment[]>();
	for (const c of comments) {
		if (!c.parent_comment_id) continue;
		const list = repliesByParent.get(c.parent_comment_id) ?? [];
		list.push(c);
		repliesByParent.set(c.parent_comment_id, list);
	}
	return roots
		.sort(
			(a, b) =>
				new Date(a.created_at).getTime() -
				new Date(b.created_at).getTime(),
		)
		.map((root) => ({
			root,
			replies: (repliesByParent.get(root.id) ?? []).sort(
				(a, b) =>
					new Date(a.created_at).getTime() -
					new Date(b.created_at).getTime(),
			),
		}));
}

export function TaskComments({ projectId, taskId }: Props) {
	const { user } = useAuth();
	const { can } = usePermission();
	const canComment = can("Create & edit tasks");
	const {
		data,
		error: loadError,
		isLoading: loading,
		mutate,
	} = useApiSWR<TaskComment[]>(["tasks", taskId, "comments"], () =>
		listTaskComments(projectId, taskId),
	);
	const items = useMemo(() => data ?? [], [data]);
	const { members } = useProjectMembers(projectId);
	const [body, setBody] = useState("");
	const [bodyMentions, setBodyMentions] = useState<string[]>([]);
	const [bodyFiles, setBodyFiles] = useState<File[]>([]);
	const [submitting, setSubmitting] = useState(false);
	const [replyTo, setReplyTo] = useState<string | null>(null);
	const [replyBody, setReplyBody] = useState("");
	const [replyMentions, setReplyMentions] = useState<string[]>([]);
	const [replyFiles, setReplyFiles] = useState<File[]>([]);
	const [replySubmitting, setReplySubmitting] = useState(false);
	const [deletingId, setDeletingId] = useState<string | null>(null);

	const threads = useMemo(() => buildThreads(items), [items]);

	async function handleSubmit() {
		const trimmed = body.trim();
		if (!trimmed && bodyFiles.length === 0) return;
		setSubmitting(true);
		try {
			const created = await createTaskComment(
				projectId,
				taskId,
				trimmed,
				null,
				bodyMentions,
			);
			if (bodyFiles.length > 0) {
				created.attachments = await uploadFiles(
					projectId,
					taskId,
					created.id,
					bodyFiles,
				);
			}
			await mutate((curr) => [...(curr ?? []), created], {
				revalidate: false,
			});
			setBody("");
			setBodyMentions([]);
			setBodyFiles([]);
		} catch (e) {
			toast.error("Failed to post comment", {
				description: (e as Error).message,
			});
		} finally {
			setSubmitting(false);
		}
	}

	async function handleReply(parentId: string) {
		const trimmed = replyBody.trim();
		if (!trimmed && replyFiles.length === 0) return;
		setReplySubmitting(true);
		try {
			const created = await createTaskComment(
				projectId,
				taskId,
				trimmed,
				parentId,
				replyMentions,
			);
			if (replyFiles.length > 0) {
				created.attachments = await uploadFiles(
					projectId,
					taskId,
					created.id,
					replyFiles,
				);
			}
			await mutate((curr) => [...(curr ?? []), created], {
				revalidate: false,
			});
			setReplyBody("");
			setReplyMentions([]);
			setReplyFiles([]);
			setReplyTo(null);
		} catch (e) {
			toast.error("Failed to post reply", {
				description: (e as Error).message,
			});
		} finally {
			setReplySubmitting(false);
		}
	}

	async function handleDeleteAttachment(
		comment: TaskComment,
		attachment: CommentAttachment,
	) {
		try {
			await deleteCommentAttachment(
				projectId,
				taskId,
				comment.id,
				attachment.id,
			);
			await mutate(
				(curr) =>
					(curr ?? []).map((c) =>
						c.id === comment.id
							? {
									...c,
									attachments: (c.attachments ?? []).filter(
										(a) => a.id !== attachment.id,
									),
								}
							: c,
					),
				{ revalidate: false },
			);
		} catch (e) {
			toast.error("Failed to delete attachment", {
				description: (e as Error).message,
			});
		}
	}

	async function handleDelete(c: TaskComment) {
		if (!window.confirm("Delete this comment?")) return;
		setDeletingId(c.id);
		await mutate(
			(curr) =>
				(curr ?? []).filter(
					(x) => x.id !== c.id && x.parent_comment_id !== c.id,
				),
			{ revalidate: false },
		);
		try {
			await deleteTaskComment(projectId, taskId, c.id);
		} catch (e) {
			toast.error("Failed to delete comment", {
				description: (e as Error).message,
			});
		} finally {
			setDeletingId(null);
			await mutate();
		}
	}

	if (loading) {
		return (
			<div className="flex items-center justify-center py-6">
				<Loader2 className="h-5 w-5 animate-spin text-muted" />
			</div>
		);
	}

	if (loadError && items.length === 0) {
		return (
			<p className="text-xs text-danger">
				Failed to load comments. {loadError.message}
			</p>
		);
	}

	return (
		<div className="space-y-4">
			{threads.length === 0 && (
				<p className="text-xs text-muted italic">No comments yet.</p>
			)}
			{threads.map((thread) => (
				<div key={thread.root.id} className="space-y-2">
					<CommentRow
						comment={thread.root}
						members={members}
						currentUserId={user?.id ?? null}
						canReply={canComment}
						deleting={deletingId === thread.root.id}
						onReply={() => {
							setReplyTo(thread.root.id);
							setReplyBody("");
							setReplyMentions([]);
							setReplyFiles([]);
						}}
						onDelete={() => handleDelete(thread.root)}
						onDeleteAttachment={(a) =>
							handleDeleteAttachment(thread.root, a)
						}
					/>
					{thread.replies.length > 0 && (
						<div className="ml-10 space-y-2 border-l border-border pl-3">
							{thread.replies.map((r) => (
								<CommentRow
									key={r.id}
									comment={r}
									members={members}
									currentUserId={user?.id ?? null}
									canReply={false}
									deleting={deletingId === r.id}
									onReply={() => {}}
									onDelete={() => handleDelete(r)}
									onDeleteAttachment={(a) =>
										handleDeleteAttachment(r, a)
									}
								/>
							))}
						</div>
					)}
					{replyTo === thread.root.id && canComment && (
						<div className="ml-10 pl-3 border-l border-border pt-2">
							<CommentComposer
								projectId={projectId}
								value={replyBody}
								submitting={replySubmitting}
								placeholder="Write a reply..."
								submitLabel="Reply"
								autoFocus
								files={replyFiles}
								onFilesChange={setReplyFiles}
								onChange={(v, ids) => {
									setReplyBody(v);
									setReplyMentions(ids);
								}}
								onCancel={() => {
									setReplyTo(null);
									setReplyBody("");
									setReplyMentions([]);
									setReplyFiles([]);
								}}
								onSubmit={() => handleReply(thread.root.id)}
							/>
						</div>
					)}
				</div>
			))}

			{canComment && (
				<div className="pt-2">
					<CommentComposer
						projectId={projectId}
						value={body}
						submitting={submitting}
						files={bodyFiles}
						onFilesChange={setBodyFiles}
						onChange={(v, ids) => {
							setBody(v);
							setBodyMentions(ids);
						}}
						onSubmit={handleSubmit}
					/>
				</div>
			)}
		</div>
	);
}

// Turns "@Full Name" tokens for mentioned users into mention links so the
// markdown preview renders them as chips.
// ponytail: also rewrites tokens inside code spans, tokenize the markdown if that shows up
function linkMentions(
	body: string,
	mentionedIds: string[],
	members: ProjectMember[],
): string {
	const names = mentionedIds
		.map((id) => members.find((m) => m.user_id === id))
		// Match whichever token was inserted: full_name or email fallback.
		.map((m) => m?.profiles.full_name?.trim() || m?.profiles.email)
		.filter((label): label is string => Boolean(label))
		.sort((a, b) => b.length - a.length);
	if (names.length === 0) return body;

	const escaped = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
	const re = new RegExp(`@(${escaped.join("|")})`, "g");
	return body.replace(re, (token) => `[${token}](${MENTION_HREF})`);
}

function CommentRow({
	comment,
	members,
	currentUserId,
	canReply,
	deleting,
	onReply,
	onDelete,
	onDeleteAttachment,
}: {
	comment: TaskComment;
	members: ProjectMember[];
	currentUserId: string | null;
	canReply: boolean;
	deleting: boolean;
	onReply: () => void;
	onDelete: () => void;
	onDeleteAttachment: (attachment: CommentAttachment) => void;
}) {
	const author = comment.author;
	const isOwn = author?.id === currentUserId;
	const attachments = comment.attachments ?? [];
	const initials = getInitials(author?.full_name ?? author?.email);
	const color = author ? profileColorClass(author.id) : "bg-muted";

	return (
		<div className="flex items-start gap-2.5">
			<Avatar className="h-7 w-7 shrink-0">
				{author?.avatar_url ? (
					<AvatarImage src={author.avatar_url} />
				) : null}
				<AvatarFallback className={cn("text-[10px] text-white", color)}>
					{initials}
				</AvatarFallback>
			</Avatar>
			<div className="flex-1 min-w-0">
				<div className="flex items-center gap-2 text-xs">
					<span className="font-semibold text-foreground truncate">
						{author?.full_name || author?.email || "Unknown"}
					</span>
					<span className="text-muted">
						{timeAgo(comment.created_at)}
					</span>
				</div>
				<MarkdownPreview
					className="mt-0.5 text-foreground"
					source={linkMentions(
						comment.body,
						comment.mentioned_user_ids ?? [],
						members,
					)}
				/>
				{attachments.length > 0 && (
					<ul className="mt-2 flex flex-wrap gap-2">
						{attachments.map((a) => (
							<li
								key={a.id}
								className="group flex items-center gap-1.5 rounded-md border border-border bg-muted-subtle px-2 py-1 text-xs"
							>
								<Paperclip className="h-3 w-3 text-muted shrink-0" />
								<a
									href={a.file_url}
									target="_blank"
									rel="noopener noreferrer"
									download={a.file_name}
									className="max-w-[12rem] truncate text-foreground hover:text-primary inline-flex items-center gap-1"
								>
									{a.file_name}
									<Download className="h-3 w-3 opacity-0 group-hover:opacity-100" />
								</a>
								<span className="text-muted">
									{formatBytes(a.file_size)}
								</span>
								{isOwn && (
									<button
										type="button"
										onClick={() => onDeleteAttachment(a)}
										className="text-muted hover:text-danger"
										aria-label={`Remove ${a.file_name}`}
									>
										<X className="h-3 w-3" />
									</button>
								)}
							</li>
						))}
					</ul>
				)}
				<div className="flex items-center gap-3 mt-1">
					{canReply && (
						<button
							type="button"
							onClick={onReply}
							className="text-xs! text-muted hover:text-primary inline-flex items-center gap-1"
						>
							<Reply className="h-3 w-3" />
							<span className="text-xs">Reply</span>
						</button>
					)}
					{isOwn && (
						<button
							type="button"
							onClick={onDelete}
							disabled={deleting}
							className="text-xs! text-muted hover:text-danger inline-flex items-center gap-1 disabled:opacity-50"
						>
							{deleting ? (
								<Loader2 className="h-3 w-3 animate-spin" />
							) : (
								<Trash2 className="h-3 w-3" />
							)}
							<span className="text-xs">Delete</span>
						</button>
					)}
				</div>
			</div>
		</div>
	);
}
